import { CaretLeft, CaretRight, Clock, X } from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { QuickWhenResult } from "~/api/opsTypes";
import { formatWhen, parseWhen, type Recurrence } from "~/features/automation/parseWhen";
import { depths } from "~/foundation/styles/depths";
import { cn } from "~/foundation/utils/cn";

export interface QuickWhenPickerProps {
  /** The trigger element's bounding rect; null falls back to a centred position (§A.2). */
  anchor: DOMRect | null;
  defaultNote?: string;
  /** Enables the "Before due" instant chip. */
  dueDate?: Date | null;
  onSet: (result: QuickWhenResult) => void;
  onClose: () => void;
}

const TIME_ZONE =
  typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC" : "UTC";

const TIME_CHIPS = [
  { label: "9:00", hour: 9, minute: 0 },
  { label: "12:00", hour: 12, minute: 0 },
  { label: "15:00", hour: 15, minute: 0 },
  { label: "18:00", hour: 18, minute: 0 },
];

const WEEKDAY_INITIALS = ["M", "T", "W", "T", "F", "S", "S"];

function startOfMonth(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1);
}

/**
 * "Remind me about this" — QuickWhen's write target is always `nudges.create`. Never more
 * than two clicks: Layer 1 instant chips commit and close in one; Layer 2 is a free-text
 * preview; Layer 3 is the full grid for anything more specific.
 *
 * A popover, not a page — positioned from the trigger's own rect (never Radix's own collision
 * logic, which this surface's exact clamping math predates and must keep matching kms).
 */
export function QuickWhenPicker({
  anchor,
  defaultNote,
  dueDate,
  onSet,
  onClose,
}: QuickWhenPickerProps) {
  const { t } = useTranslation();
  const now = useMemo(() => new Date(), []);
  const [text, setText] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [hour, setHour] = useState(9);
  const [minute, setMinute] = useState(0);
  const [recurrence, setRecurrence] = useState<Recurrence | "">("");
  const [note, setNote] = useState(defaultNote ?? "");
  const [viewMonth, setViewMonth] = useState(() => startOfMonth(now));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const parsed = useMemo(() => (text.trim() ? parseWhen(text, now) : null), [text, now]);

  const commit = (date: Date, rec: Recurrence | "") => {
    onSet({
      whenUtc: date.toISOString(),
      timezone: TIME_ZONE,
      ...(rec ? { recurrenceRule: `FREQ=${rec}` } : {}),
      ...(note.trim() ? { note: note.trim() } : {}),
    });
  };

  const commitFromGrid = () => {
    const base = selectedDate ?? parsed?.date ?? new Date(now.getTime() + 3600_000);
    const date = new Date(base);
    date.setHours(hour, minute, 0, 0);
    commit(date, recurrence);
  };

  const chips: Array<{ label: string; make: () => Date }> = [
    {
      label: t("Later today"),
      make: () => {
        const d = new Date(now);
        d.setHours(Math.min(d.getHours() + 3, 20), 0, 0, 0);
        return d;
      },
    },
    {
      label: t("Tomorrow 9:00"),
      make: () => {
        const d = new Date(now);
        d.setDate(d.getDate() + 1);
        d.setHours(9, 0, 0, 0);
        return d;
      },
    },
    {
      label: t("Next Monday"),
      make: () => {
        const d = new Date(now);
        const diff = (1 - d.getDay() + 7) % 7 || 7;
        d.setDate(d.getDate() + diff);
        d.setHours(9, 0, 0, 0);
        return d;
      },
    },
  ];
  if (dueDate) {
    chips.push({
      label: t("Before due"),
      make: () => {
        const d = new Date(dueDate);
        d.setDate(d.getDate() - 1);
        d.setHours(9, 0, 0, 0);
        return d;
      },
    });
  }

  const first = startOfMonth(viewMonth);
  const leadBlanks = (first.getDay() + 6) % 7; // Monday-first.
  const daysInMonth = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0).getDate();
  const cells: Array<{ key: string; date: Date | null }> = [
    ...Array.from({ length: leadBlanks }, (_, blankIndex) => ({
      key: `blank-${viewMonth.getFullYear()}-${viewMonth.getMonth()}-${blankIndex}`,
      date: null,
    })),
    ...Array.from({ length: daysInMonth }, (_, dayIndex) => ({
      key: `day-${viewMonth.getFullYear()}-${viewMonth.getMonth()}-${dayIndex + 1}`,
      date: new Date(viewMonth.getFullYear(), viewMonth.getMonth(), dayIndex + 1),
    })),
  ];

  const style = anchor
    ? {
        top: Math.min(anchor.bottom + 8, window.innerHeight - 460),
        insetInlineStart: Math.max(8, Math.min(anchor.left, window.innerWidth - 328)),
      }
    : { top: "20vh", insetInlineStart: "50%", transform: "translateX(-50%)" };

  return createPortal(
    <>
      <button
        type="button"
        aria-label={t("Close")}
        onMouseDown={onClose}
        className="fixed inset-0 cursor-default bg-transparent"
        style={{ zIndex: depths.menu }}
      />
      <div
        role="dialog"
        aria-label={t("Nudge me")}
        className="slate-pop fixed flex w-80 flex-col gap-2 rounded-md border border-divider bg-menu-bg p-2.5 shadow-menu"
        style={{ ...style, zIndex: depths.menu + 1 }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5 text-body font-semibold text-text">
            <Clock size={14} aria-hidden />
            {t("Nudge me")}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("Close")}
            className="flex size-7 items-center justify-center rounded-sm text-text-secondary hover:bg-hover-bg"
          >
            <X size={15} aria-hidden />
          </button>
        </div>

        {/* Layer 1 — instant chips: one click, commit and close. */}
        <div className="flex flex-wrap gap-1.5">
          {chips.map((chip) => (
            <button
              key={chip.label}
              type="button"
              onClick={() => commit(chip.make(), "")}
              className="h-7 rounded-full border border-divider bg-bg-secondary px-2.5 text-small text-text hover:border-accent hover:text-accent"
            >
              {chip.label}
            </button>
          ))}
        </div>

        {/* Layer 2 — natural-language input with a live preview. */}
        <input
          ref={inputRef}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && parsed) {
              event.preventDefault();
              commit(parsed.date, parsed.recurrence ?? "");
            }
          }}
          placeholder={t('"fri 14:00", "in 3 days", "every monday"…')}
          className="h-8 rounded-sm border border-input-border bg-bg px-2.5 text-small text-text focus:border-input-border-focus focus:outline-none"
        />
        {text.trim() ? (
          <p className={cn("px-0.5 text-small", parsed ? "text-accent" : "text-text-tertiary")}>
            {parsed
              ? `→ ${formatWhen(parsed.date)}${
                  parsed.recurrence ? ` · ${t("every")} ${parsed.recurrence.toLowerCase()}` : ""
                }`
              : t("Couldn't read that time")}
          </p>
        ) : null}

        {/* Layer 3 — mini month grid + time chips + repeat + note. */}
        <div className="mt-0.5 flex items-center justify-between">
          <button
            type="button"
            onClick={() =>
              setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))
            }
            aria-label={t("Previous month")}
            className="flex size-6 items-center justify-center rounded-sm text-text-secondary hover:bg-hover-bg"
          >
            <CaretLeft size={14} aria-hidden className="rtl:rotate-180" />
          </button>
          <span className="text-small font-semibold text-text">
            {viewMonth.toLocaleString(undefined, { month: "long", year: "numeric" })}
          </span>
          <button
            type="button"
            onClick={() =>
              setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))
            }
            aria-label={t("Next month")}
            className="flex size-6 items-center justify-center rounded-sm text-text-secondary hover:bg-hover-bg"
          >
            <CaretRight size={14} aria-hidden className="rtl:rotate-180" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-0.5">
          {WEEKDAY_INITIALS.map((label, position) => (
            <span
              // The header has exactly seven fixed cells and the letters repeat ("M","T","W",
              // "T","F","S","S"), so a position-derived key is the only stable one available.
              key={`dow-${WEEKDAY_INITIALS.slice(0, position + 1).join("")}`}
              className="py-0.5 text-center text-small text-text-tertiary"
            >
              {label}
            </span>
          ))}
          {cells.map(({ key, date: cell }) =>
            cell ? (
              <button
                key={key}
                type="button"
                onClick={() => setSelectedDate(cell)}
                className={cn(
                  "h-7 rounded-sm text-small",
                  selectedDate?.toDateString() === cell.toDateString()
                    ? "bg-accent text-accent-text"
                    : cell.toDateString() === now.toDateString()
                      ? "font-bold text-accent hover:bg-bg-secondary"
                      : "text-text hover:bg-bg-secondary",
                )}
              >
                {cell.getDate()}
              </button>
            ) : (
              <span key={key} />
            ),
          )}
        </div>

        <div className="flex items-center gap-2">
          <span className="w-12 text-small text-text-tertiary">{t("Time")}</span>
          <div className="flex gap-1.5">
            {TIME_CHIPS.map((chip) => (
              <button
                key={chip.label}
                type="button"
                onClick={() => {
                  setHour(chip.hour);
                  setMinute(chip.minute);
                }}
                className={cn(
                  "h-7 rounded-full border px-2 text-small",
                  hour === chip.hour && minute === chip.minute
                    ? "border-accent bg-accent text-accent-text"
                    : "border-divider text-text",
                )}
              >
                {chip.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-2">
          <span className="w-12 text-small text-text-tertiary">{t("Repeat")}</span>
          <select
            value={recurrence}
            onChange={(event) => setRecurrence(event.target.value as Recurrence | "")}
            className="h-7 flex-1 rounded-sm border border-input-border bg-bg px-2 text-small text-text"
          >
            <option value="">{t("None")}</option>
            <option value="DAILY">{t("Daily")}</option>
            <option value="WEEKLY">{t("Weekly")}</option>
            <option value="MONTHLY">{t("Monthly")}</option>
          </select>
        </div>

        <div className="mt-0.5 flex items-center gap-2 border-t border-divider pt-2">
          <input
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder={t("Note (optional)")}
            className="h-8 flex-1 rounded-sm border border-input-border bg-bg px-2.5 text-small text-text focus:border-input-border-focus focus:outline-none"
          />
          <button
            type="button"
            onClick={commitFromGrid}
            className="h-8 shrink-0 rounded-sm bg-accent px-3.5 text-small font-semibold text-accent-text hover:opacity-90"
          >
            {t("Set nudge")}
          </button>
        </div>
      </div>
    </>,
    document.body,
  );
}
